import { AvisoDeSucessoDaChegada } from "../componentes/avisos/AvisoDeSucessoDaChegada";
import { useState } from "react";
import { Link } from "react-router";
import { Abas, Aviso, Cartao, Chip, PainelDaAba } from "../componentes/ui";
import { AvisosDoPainel } from "../componentes/avisos/AvisosDoPainel";
import { EsteiraDaImplantacao, ResumoDaImplantacao } from "../componentes/esteira";
import { resumoDoAndamentoDosModulos } from "../componentes/esteira/AndamentoDosModulos";
import { PainelDeSaude } from "../componentes/PainelDeSaude";
import { ehInterno, podeNaSessao, useSessao } from "../nucleo/sessao";
import { useTitulo } from "../nucleo/titulo";
import { CAMINHO_DO_CONTRATO_DO_CLIENTE, PERMISSAO_DA_TELA_CONTRATO } from "../nucleo/contratoDoCliente";
import {
  ROTULO_DA_VEZ,
  formatarCnpj,
  formatarData,
  useMinhaEsteira,
  type Esteira,
} from "../nucleo/implantacoes";
import {
  CAMINHO_DAS_REUNIOES_DO_CLIENTE,
  formatarDuracao,
  formatarQuando,
  registrarEntradaNaReuniao,
  rotuloDoTipoDeReuniao,
  useReunioes,
} from "../nucleo/reuniao";

type ChaveDaAba = "implantacao" | "acesso";

const PERMISSAO_DE_VER_A_ESTEIRA = "esteira:ver";

const ABAS = [
  { chave: "implantacao", rotulo: "Minha implantação" },
  { chave: "acesso", rotulo: "Meu acesso" },
] as const satisfies ReadonlyArray<{ chave: ChaveDaAba; rotulo: string }>;

function primeiroNome(nome: string): string {
  return nome.trim().split(/\s+/)[0] ?? nome;
}

/**
 * O painel do cliente — a tela 3 do protótipo.
 *
 * A primeira coisa que o cliente vê: onde ele está, de quem é a vez e o que vem depois. Tudo
 * derivado da linha do tempo no servidor; esta tela não recalcula etapa nenhuma.
 *
 * A esteira nunca é pedida por id. `GET /api/implantacoes/minha` resolve a implantação pela
 * organização da sessão — não existe parâmetro para trocar, e é isso que torna impossível, do
 * lado do navegador, pedir a esteira de outra empresa.
 */
export function PainelDoCliente() {
  useTitulo("Minha implantação");
  const [aba, setAba] = useState<ChaveDaAba>("implantacao");
  const sessao = useSessao();
  /* A consulta não tem `esteira:ver` pela matriz da spec §4. Perguntar mesmo assim só colhia um
     403 e a tela pedia para "atualizar em alguns instantes" — uma espera que nunca termina. */
  const podeVerEsteira = podeNaSessao(sessao.data, PERMISSAO_DE_VER_A_ESTEIRA);
  const esteira = useMinhaEsteira(podeVerEsteira);

  const nome = sessao.data ? primeiroNome(sessao.data.usuario.nome) : null;

  return (
    <>
      <AvisoDeSucessoDaChegada />
      <h1 className="titulo-pagina">
        {nome ? <>Olá, <strong>{nome}</strong></> : <>Portal de <strong>Implantação</strong></>}
      </h1>
      <p className="lead">
        Sua implantação tem cinco etapas. Você pode parar e voltar quando quiser — tudo fica
        salvo.
      </p>

      {/* O mural da GEOvendas: só aparece quando há aviso no ar para a empresa. */}
      <AvisosDoPainel />

      <Abas<ChaveDaAba>
        id="painel"
        rotulo="Seções da área do cliente"
        itens={ABAS}
        ativa={aba}
        aoSelecionar={setAba}
        className="abas--embutida mb-5"
      />

      <PainelDaAba id="painel" chave="implantacao" ativa={aba}>
        {/* Só com a sessão resolvida a falta de permissão é uma afirmação sobre esta pessoa;
            antes disso o aviso de consulta piscava para qualquer usuário. */}
        {sessao.data && !podeVerEsteira ? (
          <Aviso tom="marca" data-testid="esteira-fora-do-acesso">
            <b>O seu acesso é de consulta.</b> Ele abre as atas das reuniões e as respostas do
            kickoff, mas não o andamento das etapas. Se você precisa acompanhá-lo, peça a quem
            administra o acesso da sua empresa.
          </Aviso>
        ) : esteira.isPending ? (
          <p className="lead" role="status">
            Carregando a sua implantação…
          </p>
        ) : esteira.isError ? (
          <Aviso tom="erro" data-testid="falha-da-esteira">
            Não foi possível carregar a sua implantação agora. Atualize a página em alguns
            instantes; se continuar, fale com o consultor responsável.
          </Aviso>
        ) : esteira.data ? (
          <Implantacao esteira={esteira.data} />
        ) : (
          <SemImplantacao interno={sessao.data ? ehInterno(sessao.data) : false} />
        )}
      </PainelDaAba>

      <PainelDaAba id="painel" chave="acesso" ativa={aba}>
        <CartaoDaSessao />
        <ComoPedirAcesso contato={esteira.data?.contato ?? null} />
        {/* O estado da API, do banco e do cofre é diagnóstico nosso, não informação do
            cliente: a linha só tem valor quando diz "não está", e quem está lendo a tela já
            provou que o portal está no ar. Decisão da consultoria em 01/09/2026. Fica para
            quem é interno, que é quem faz alguma coisa com a resposta. */}
        {sessao.data && ehInterno(sessao.data) ? <PainelDeSaude /> : null}
      </PainelDaAba>
    </>
  );
}

function Implantacao({ esteira }: { esteira: Esteira }) {
  const sessao = useSessao();
  const assinatura = formatarData(esteira.prazo.contrato_assinado_em);
  const resumoDosModulos = resumoDoAndamentoDosModulos(esteira);

  return (
    <>
      <p className="lead" data-testid="proximo-passo">
        <b>{ROTULO_DA_VEZ[esteira.vez]}.</b> {esteira.proximo_passo}
      </p>
      {resumoDosModulos ? <p className="lead" data-testid="resumo-dos-modulos-topo">{resumoDosModulos}</p> : null}

      <ResumoDaImplantacao esteira={esteira} />
      <EsteiraDaImplantacao esteira={esteira} />

      {esteira.pendencias.length > 0 ? (
        <section className="mb-6" aria-labelledby="titulo-pendencias">
          <h2 className="titulo-secao" id="titulo-pendencias">
            Pendências
          </h2>
          <div className="grid gap-3 md:grid-cols-2" data-testid="pendencias">
            {esteira.pendencias.map((pendencia) => (
              <Cartao
                key={pendencia.referencia}
                titulo={pendencia.titulo}
                acessorio={
                  <Chip estado={pendencia.de_quem === "cliente" ? "atencao" : "neutro"}>
                    {pendencia.de_quem === "cliente" ? "com você" : "com a GEOvendas"}
                  </Chip>
                }
              >
                {pendencia.descricao ? (
                  <p className="cartao__texto">{pendencia.descricao}</p>
                ) : null}
              </Cartao>
            ))}
          </div>
        </section>
      ) : null}

      <ChamadaDasReunioes implantacaoId={esteira.implantacao_id} />

      <ContatoNaGeovendas contato={esteira.contato} consultor={esteira.consultor} />

      <div className="grid gap-3 md:grid-cols-2">
        <Cartao titulo="O que você contratou">
          <ul className="lista-de-marcas" data-testid="lista-de-modulos">
            {esteira.modulos.map((modulo) => (
              <li className="lista-de-marcas__item" key={modulo.chave}>
                {modulo.nome}
              </li>
            ))}
          </ul>
          {esteira.recursos_plus.length > 0 ? (
            <>
              <p className="campo__rotulo mt-4">Recursos Plus</p>
              <ul className="lista-de-marcas">
                {esteira.recursos_plus.map((recurso) => (
                  <li className="lista-de-marcas__item" key={recurso.chave}>
                    {recurso.nome}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          {podeNaSessao(sessao.data, PERMISSAO_DA_TELA_CONTRATO) ? (
            <p className="mt-3 text-sm">
              <Link to={CAMINHO_DO_CONTRATO_DO_CLIENTE} data-testid="ver-contrato">
                Ver o contrato, as datas e o arquivo assinado
              </Link>
            </p>
          ) : null}
          <p className="nota-rodape mt-3">
            Você só responde o kickoff dos módulos contratados — as outras trilhas nem aparecem.
          </p>
        </Cartao>

        <Cartao titulo="Sua implantação">
          <dl className="grid gap-3 m-0">
            <div>
              <dt className="campo__rotulo">Empresa</dt>
              <dd className="m-0 text-sm" data-testid="empresa-da-esteira">
                {esteira.organizacao.nome_fantasia ?? esteira.organizacao.razao_social} ·{" "}
                <span className="numero-tabular">
                  {formatarCnpj(esteira.organizacao.cnpj)}
                </span>
              </dd>
            </div>
            <div>
              <dt className="campo__rotulo">Contrato assinado em</dt>
              <dd className="m-0 text-sm numero-tabular">{assinatura ?? "—"}</dd>
            </div>
          </dl>
        </Cartao>
      </div>
    </>
  );
}

type PessoaDaGeovendas = NonNullable<Esteira["contato"]>;

/**
 * Quem o cliente procura. Hoje o servidor devolve a mesma pessoa em `contato` e `consultor` (o
 * consultor da conta); ela aparece uma vez só. Os dois papéis só se separam quando o servidor
 * mandar pessoas diferentes.
 */
function ContatoNaGeovendas({ contato, consultor }: { contato: Esteira["contato"]; consultor: Esteira["consultor"] }) {
  const unica = contato ?? consultor;
  const pessoas: Array<[string, PessoaDaGeovendas]> =
    contato && consultor && contato.id !== consultor.id
      ? [["Implantação", contato], ["Comercial", consultor]]
      : unica
        ? [["Consultor da conta", unica]]
        : [];

  return (
    <Cartao className="mb-6" titulo="Seu contato na GEOvendas" data-testid="contato-da-implantacao">
      {pessoas.length === 0 ? <p className="cartao__texto">a definir pela GEOvendas</p> : (
        <div className="grid gap-4 md:grid-cols-2">
          {pessoas.map(([papel, pessoa]) => (
            <div key={papel}>
              <p className="cartao__texto"><b>{pessoa.nome}</b></p>
              <p className="cartao__texto">{papel}</p>
              {pessoa.telefone ? (
                <p className="cartao__texto">
                  <a href={`tel:${pessoa.telefone.replace(/[^+\d]/g, "")}`}>{pessoa.telefone}</a>
                </p>
              ) : null}
              <p className="cartao__texto"><a href={`mailto:${pessoa.email}`}>{pessoa.email}</a></p>
            </div>
          ))}
        </div>
      )}
    </Cartao>
  );
}

/** A próxima reunião na página inicial; o histórico inteiro continua na Agenda (spec 5.6). */
function ChamadaDasReunioes({ implantacaoId }: { implantacaoId: string }) {
  const reunioes = useReunioes(implantacaoId);
  // O servidor já entrega na ordem do calendário. O estado, e não o relógio do navegador,
  // decide o que ainda está por acontecer: remarcar e realizar são atos registrados no portal.
  const proxima = reunioes.data?.reunioes.find((reuniao) => reuniao.estado === "agendada");

  return (
    <Cartao className="mb-6" titulo="Próxima reunião" data-testid="reunioes-no-painel">
      {reunioes.isPending ? (
        <p className="cartao__texto" role="status">
          Conferindo a agenda…
        </p>
      ) : reunioes.isError ? (
        <p className="cartao__texto" data-testid="falha-da-proxima-reuniao">
          Não foi possível conferir a próxima reunião agora. A Agenda continua disponível pelo
          atalho abaixo.
        </p>
      ) : proxima ? (
        <div data-testid="proxima-reuniao">
          <p className="cartao__texto">
            <b>{rotuloDoTipoDeReuniao(proxima.tipo)}</b> · {formatarQuando(proxima.agendada_para)} ·{" "}
            {formatarDuracao(proxima.duracao_min)}
          </p>
          {proxima.link ? (
            <p className="nota-rodape mt-2">
              <a
                href={proxima.link}
                target="_blank"
                rel="noreferrer noopener"
                onClick={() => registrarEntradaNaReuniao(proxima.id)}
              >
                Entrar na reunião
              </a>
            </p>
          ) : null}
        </div>
      ) : (
        <p className="cartao__texto" data-testid="sem-proxima-reuniao">
          Nenhuma reunião está marcada agora. Quando a equipe registrar a próxima, o horário e o
          link aparecem aqui.
        </p>
      )}
      <p className="nota-rodape mt-2">
        <Link to={CAMINHO_DAS_REUNIOES_DO_CLIENTE} data-testid="ver-atas">
          Abrir a Agenda
        </Link>
      </p>
    </Cartao>
  );
}

function SemImplantacao({ interno }: { interno: boolean }) {
  return (
    <Cartao titulo="Ainda não há uma esteira aberta">
      <Aviso tom="marca" data-testid="sem-implantacao">
        {interno ? (
          <>
            <b>Você é do time da GEOvendas.</b> A esteira desta tela é a do cliente — o
            acompanhamento interno de todas as implantações fica no console, em{" "}
            <code>/admin</code>.
          </>
        ) : (
          <>
            <b>Assim que o time comercial registrar o contrato</b>, as cinco etapas aparecem
            aqui com o estado de cada uma. Nada precisa ser feito por você agora.
          </>
        )}
      </Aviso>
    </Cartao>
  );
}

/** Cartão de identidade: quem entrou, por qual organização e com quantas permissões. */
/**
 * Como pedir acesso para um colega — a decisão da fase 1 é **texto**, não fluxo de convite.
 *
 * A permissão `usuarios:gerenciar` já existe para o `admin_cliente`, então o destino é o cliente
 * administrando sozinho; só não é agora. Enquanto não é, a tela diz a quem pedir com nome e
 * e-mail de verdade — a alternativa é a pessoa não pedir a ninguém.
 *
 * **`contato`, e não `consultor`.** O servidor resolve quem é: o consultor quando já existe, o
 * responsável pela venda enquanto não existe. Este cartão lia `consultor` e caía num texto sem
 * nome nenhum no começo da implantação — que é exatamente quando o cliente mais precisa saber
 * com quem falar. Decisão da consultoria em 01/09/2026.
 */
function ComoPedirAcesso({ contato }: { contato: { nome: string; email: string } | null }) {
  return (
    <Cartao className="mb-6" titulo="Precisa dar acesso a um colega?">
      <p className="cartao__texto" data-testid="como-pedir-acesso">
        {contato ? (
          <>
            Fale com <b>{contato.nome}</b>, que acompanha a sua implantação, pelo e-mail{" "}
            <a href={`mailto:${contato.email}`}>{contato.email}</a>. Informe o nome, o e-mail e o
            que a pessoa precisa acompanhar.
          </>
        ) : (
          <>
            Fale com quem acompanha a sua implantação na GEOvendas, informando o nome, o e-mail e
            o que a pessoa precisa acompanhar.
          </>
        )}
      </p>
    </Cartao>
  );
}

function CartaoDaSessao() {
  const sessao = useSessao();
  if (!sessao.data) return null;

  const interno = ehInterno(sessao.data);

  return (
    <Cartao
      className="mb-6"
      titulo="Sua sessão"
      acessorio={
        <Chip estado={interno ? "aguardando" : "ok"} data-testid="lado-da-sessao">
          {interno ? "GEOvendas" : "cliente"}
        </Chip>
      }
    >
      <dl className="grid gap-4 sm:grid-cols-3 m-0">
        <div>
          <dt className="campo__rotulo">Quem</dt>
          <dd className="m-0 text-sm" data-testid="sessao-usuario">
            {sessao.data.usuario.nome} · {sessao.data.usuario.email}
          </dd>
        </div>
        <div>
          <dt className="campo__rotulo">Organização</dt>
          <dd className="m-0 text-sm" data-testid="sessao-organizacao">
            {sessao.data.organizacao?.nome ?? "— (usuário interno)"}
          </dd>
        </div>
        <div>
          <dt className="campo__rotulo">Papel</dt>
          <dd className="m-0 text-sm" data-testid="sessao-papel">
            {sessao.data.usuario.papeis?.[0] ?? "—"}
          </dd>
        </div>
      </dl>
      <p className="nota-rodape mt-3">
        {interno
          ? "A organização é a fronteira dos dados: ela entra em toda transação do banco e é o que a política de linha usa para decidir o que existe para você."
          : "Você só enxerga a implantação da sua empresa — é a organização acima que define isso."}
      </p>
    </Cartao>
  );
}
